import React, { type ReactElement } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import type { Project } from '@atlassian/jira-common-directory-v2/src/model/shareable-entities/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { FilterBar } from '@atlassian/jira-common-directory-v2/src/components/filter-bar/FilterBar.tsx';
import { FilterItem } from '@atlassian/jira-common-directory-v2/src/components/filter-bar/FilterItem.tsx';
import type { User } from '@atlassian/jira-user-picker/src/model/index.tsx';
import type { Filter as FilterType } from '../../../model/index.tsx';
import messages from './messages.tsx';
import type { FilterSearchProps, ProjectSearchProps, UserSearchProps } from './types.tsx';

export type Props = {
	value: FilterType;
	renderFilterSearch: (props: FilterSearchProps) => ReactElement;
	renderProjectPicker: (props: ProjectSearchProps) => ReactElement;
	renderUserPicker: (props: UserSearchProps) => ReactElement;
	onChange: (arg1: FilterType, arg2: AnalyticsEvent) => void;
};

const Filter = (props: Props): React.JSX.Element => {
	const projectPlaceholderOldGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.projectPlaceholderOldGalaxia
		: messages.projectPlaceholderOld;
	const projectPlaceholderGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.projectPlaceholderGalaxia
		: messages.projectPlaceholder;
	const { formatMessage } = useIntl();
	const { renderFilterSearch, onChange, renderProjectPicker, renderUserPicker, value } = props;

	return (
		<FilterBar>
			<Box xcss={searchWrapperStyles}>
				{renderFilterSearch({
					placeholder: formatMessage(messages.searchPlaceholder),
					value: '',
					hasAutoFocus: false,
					isCompact: true,
					increasedWidth: false,
					placeholderAlwaysVisible: true,
					isAlwaysExpanded: false,
					shouldFitContainerWidth: true,
					onChange: (text: string, analyticsEvent: AnalyticsEvent) => {
						onChange({ ...value, text }, analyticsEvent);
					},
				})}
			</Box>
			<FilterItem>
				<Box xcss={!isVisualRefreshEnabled() && filterLabelStyles}>
					{renderProjectPicker({
						onChange: (project: Project, analyticsEvent: AnalyticsEvent) => {
							onChange({ text: value.text, project, user: undefined }, analyticsEvent);
						},
						placeholder: isVisualRefreshEnabled()
							? formatMessage(projectPlaceholderGalaxia)
							: formatMessage(projectPlaceholderOldGalaxia),
						label: isVisualRefreshEnabled() ? undefined : formatMessage(messages.projectLabel),
						selectedProject: undefined,
						isCompact: true,
					})}
				</Box>
			</FilterItem>
			<FilterItem>
				<Box xcss={!isVisualRefreshEnabled() && filterLabelStyles}>
					{renderUserPicker({
						onChange: (user: User | undefined, analyticsEvent: AnalyticsEvent) => {
							onChange({ text: value.text, project: undefined, user }, analyticsEvent);
						},
						placeholder: isVisualRefreshEnabled()
							? formatMessage(messages.userLocationLabel)
							: formatMessage(messages.userLocationPlaceholder),
						label: isVisualRefreshEnabled() ? undefined : formatMessage(messages.userLocationLabel),
					})}
				</Box>
			</FilterItem>
		</FilterBar>
	);
};

const searchWrapperStyles = xcss({
	flexBasis: '200px',
});

const filterLabelStyles = xcss({
	marginTop: 'space.negative.250',
});

export default Filter;
